import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'qs';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import { ANIMATION_DURATION, Animate, Layout, LoadingScreen } from 'ds';
import { ConsumablesProduct } from 'shared/types';
import { actions } from 'store/Consumables';
import { selectProducts } from 'store/Consumables/selectors';
import {
  selectCartToastZIndex,
  selectHideToastTimeoutId,
  selectProductSubscriptions,
  selectResetZIndexTimeoutId,
  selectShowOnboardingCartToast
} from 'store/Consumables/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import ConsumablesHowItWorksSteps from './ConsumablesHowItWorksSteps';
import ConsumablesOnboardingSetPageHeaderSection from './ConsumablesOnbaordingSetPageHeaderSection';
import ConsumablesOnboardingAddToCartToast from './ConsumablesOnboardingAddToCartToast';
import ConsumablesOnboardingCartModal from './ConsumablesOnboardingCartModal';
import ConsumablesOnboardingInfoModal from './ConsumablesOnboardingInfoModal';
import ConsumablesOnboardingProductModal from './ConsumablesOnboardingProductModal';
import ConsumablesOnboardingSetPageMainContent from './ConsumablesOnboardingSetPageMainContent';
import { BASIC_PRODUCTS_MPNs, PREMIUM_PRODUCTS_MPNs } from '../constants';
import {
  createConsumablesProductSubscription,
  getConsumablesProductSubscriptions,
  getConsumablesProducts
} from '../requests';
import { EditableConsumablesOnboardingProduct } from '../types';

interface Props {}

const ConsumablesOnboardingSetPage: React.FC<Props> = () => {
  const FADE_OUT_DURATION = 800;
  const history = useHistory();
  const dispatch = useAppDispatch();
  const consumablesProducts = useAppSelector(selectProducts);
  const [isProductModalVisible, setIsProductModalVisible] = useState(false);
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ConsumablesProduct | null>(null);
  const [selectedProductQuantity, setSelectedProductQuantity] = useState(1);
  const isCartToastVisible = useAppSelector(selectShowOnboardingCartToast);
  const cartToastZIndex = useAppSelector(selectCartToastZIndex);
  const hideToastTimeoutId = useAppSelector(selectHideToastTimeoutId);
  const resetZIndexTimeoutId = useAppSelector(selectResetZIndexTimeoutId);
  const consumablesProductSubscriptions = useAppSelector(selectProductSubscriptions);
  const [isCartModalVisible, setIsCartModalVisible] = useState(false);
  const [isChangesNotSavedModalVisible, setIsChangesNotSavedModalVisible] = useState(false);
  const queryParams = qs.parse(history.location.search, { ignoreQueryPrefix: true });
  let { type } = queryParams;
  const { isMobile } = useContext(media);
  if (type !== 'basic' && type !== 'premium') {
    type = 'basic';
  }

  const selectedMpns = type === 'basic' ? BASIC_PRODUCTS_MPNs : PREMIUM_PRODUCTS_MPNs;

  const initialEditableProducts: EditableConsumablesOnboardingProduct[] = selectedMpns
    .map(mpn => {
      const product = consumablesProducts.find(p => p.mpn === mpn);
      return product
        ? {
            consumables_product: product,
            quantity: 2
          }
        : null;
    })
    .filter((product): product is EditableConsumablesOnboardingProduct => product !== null);

  const [editableProducts, setEditableProducts] = useState<EditableConsumablesOnboardingProduct[]>([]);

  const originalObj = initialEditableProducts.reduce((acc, item) => {
    acc[item.consumables_product.id] = item.quantity;
    return acc;
  }, {} as Record<number, number>);

  const clonedObj = editableProducts.reduce((acc, item) => {
    acc[item.consumables_product.id] = item.quantity;
    return acc;
  }, {} as Record<number, number>);

  const changesMade = (() => {
    const originalKeys = Object.keys(originalObj);
    const clonedKeys = Object.keys(clonedObj);

    if (originalKeys.length !== clonedKeys.length) return true;

    for (const key of originalKeys) {
      const numericKey = Number(key);
      if (originalObj[numericKey] !== clonedObj[numericKey]) return true;
    }

    return false;
  })();

  useEffect(() => {
    if (!consumablesProductSubscriptions || !consumablesProducts) {
      Promise.all([getConsumablesProducts(), getConsumablesProductSubscriptions()]).then(
        ([{ data: products }, { data: subscriptions }]) => {
          dispatch(actions.setProducts(products));
          dispatch(actions.setConsumablesProductSubscriptions(subscriptions));
        }
      );
    }
  }, [dispatch, consumablesProductSubscriptions, consumablesProducts]);

  useEffect(() => {
    // this useEffect is only needed to set the initial editable products when the page is loaded
    if (consumablesProducts.length > 0) {
      const selectedMpns = type === 'basic' ? BASIC_PRODUCTS_MPNs : PREMIUM_PRODUCTS_MPNs;
      const initialEditableProducts: EditableConsumablesOnboardingProduct[] = selectedMpns
        .map(mpn => {
          const product = consumablesProducts.find(p => p.mpn === mpn);
          return product
            ? {
                consumables_product: product,
                quantity: 2
              }
            : null;
        })
        .filter((product): product is EditableConsumablesOnboardingProduct => product !== null);

      setEditableProducts(initialEditableProducts);
    }
  }, [consumablesProducts, type]);

  if (!consumablesProducts || !consumablesProductSubscriptions) {
    return <LoadingScreen />;
  }

  const handleShowToast = () => {
    if (hideToastTimeoutId) {
      clearTimeout(hideToastTimeoutId);
    }
    if (resetZIndexTimeoutId) {
      clearTimeout(resetZIndexTimeoutId);
    }

    dispatch(actions.setCartToastZIndex(9999));
    dispatch(actions.setShowOnboardingCartToast(true));

    const newHideToastTimeoutId = setTimeout(() => {
      dispatch(actions.setShowOnboardingCartToast(false));
    }, 5000);
    dispatch(actions.setHideToastTimeoutId(newHideToastTimeoutId));

    const newResetZIndexTimeoutId = setTimeout(() => {
      dispatch(actions.setCartToastZIndex(-1));
    }, 5000 + FADE_OUT_DURATION);
    dispatch(actions.setResetZIndexTimeoutId(newResetZIndexTimeoutId));
  };

  const closeCartToast = () => {
    if (hideToastTimeoutId) {
      clearTimeout(hideToastTimeoutId);
      dispatch(actions.setHideToastTimeoutId(null));
    }
    if (resetZIndexTimeoutId) {
      clearTimeout(resetZIndexTimeoutId);
      dispatch(actions.setResetZIndexTimeoutId(null));
    }

    dispatch(actions.setShowOnboardingCartToast(false));
    setTimeout(() => {
      dispatch(actions.setCartToastZIndex(-1));
    }, FADE_OUT_DURATION);
  };

  const handleCloseProductModal = () => {
    setIsProductModalVisible(false);
    setSelectedProductQuantity(1);
    setTimeout(() => {
      setSelectedProduct(null);
    }, ANIMATION_DURATION);
  };

  const handleAddProductFromModal = async () => {
    if (selectedProduct == null || selectedProductQuantity <= 0) return;
    try {
      const response = await createConsumablesProductSubscription(selectedProduct.id, selectedProductQuantity);
      if (response.status === 200) {
        dispatch(actions.setConsumablesProductSubscriptions(response.data.consumables_product_subscriptions));
        handleCloseProductModal();
        handleShowToast();
      } else {
        showSnackbar({
          message: 'Error adding product. Please try again.',
          negative: true
        });
      }
    } catch (error) {
      showSnackbar({
        message: 'Error adding product. Please try again.',
        negative: true
      });
    }
  };

  return (
    <Layout direction="column" width="100%" height="100%" align={isMobile ? undefined : 'center'}>
      <ConsumablesOnboardingSetPageHeaderSection
        setIsChangesNotSavedModalVisible={setIsChangesNotSavedModalVisible}
        onCartClick={() => setIsCartModalVisible(true)}
        type={type as 'basic' | 'premium'}
        onInfoButtonClick={() => setIsInfoModalVisible(true)}
        hasChanges={changesMade}
      />
      <ConsumablesHowItWorksSteps />
      <ConsumablesOnboardingSetPageMainContent
        editableProducts={editableProducts}
        setEditableProducts={setEditableProducts}
        setIsChangesNotSavedModalVisible={setIsChangesNotSavedModalVisible}
        isChangesNotSavedModalVisible={isChangesNotSavedModalVisible}
        handleShowToast={handleShowToast}
        onProductCardClick={product => {
          setSelectedProduct(product);
          setIsProductModalVisible(true);
        }}
        onRestockingBannerClick={isMobile ? () => setIsInfoModalVisible(true) : undefined}
      />
      <ConsumablesOnboardingCartModal isVisible={isCartModalVisible} onClose={() => setIsCartModalVisible(false)} />
      {!isProductModalVisible && !isCartModalVisible && (
        <Animate
          animation="fade-in-from-top"
          visible={isCartToastVisible}
          fadeOut
          position="fixed"
          right={isMobile ? 24 : 40}
          top={80}
          zIndex={cartToastZIndex}
        >
          <ConsumablesOnboardingAddToCartToast
            closeCartToast={closeCartToast}
            onPlusMoreItemsClick={() => {
              closeCartToast();
              setIsCartModalVisible(true);
            }}
          />
        </Animate>
      )}
      <ConsumablesOnboardingProductModal
        isProductModalVisible={isProductModalVisible}
        onClose={handleCloseProductModal}
        selectedProduct={selectedProduct}
        handleAddProduct={handleAddProductFromModal}
        selectedProductQuantity={selectedProductQuantity}
        setSelectedProductQuantity={setSelectedProductQuantity}
      />
      <ConsumablesOnboardingInfoModal isVisible={isInfoModalVisible} onClose={() => setIsInfoModalVisible(false)} />
    </Layout>
  );
};

export default ConsumablesOnboardingSetPage;
